import React, { ChangeEvent, FocusEvent, useContext, useEffect } from 'react';

import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';

import Spinner from 'components/common/Spinner/Spinner';
import { RootStoreContext } from 'index';
import TextInput from 'components/common/TextInput/TextInput';
import PasswordInput from 'components/common/PasswordInput/PasswordInput';
import Button from 'components/common/Button/Button';
import { notify } from 'components/common/Toast/toast.helpers';
import PasswordValidationPoints from 'components/common/PasswordValidationPoints/PasswordValidationPoints';
import { ApplicationRoute } from 'routes/routes.enums';
import useForm, { FieldValues } from 'hooks/useForm';
import { validatePassword, validateRequiredField } from 'common/utils/validation.utils';

import styles from './SignUpPage.module.scss';
import { SignUpFieldName } from './signUpPage.enums';

const SignUpPage = () => {
  const navigator = useNavigate();
  const { authStore } = useContext(RootStoreContext);
  const {
    authStore: { isLoading, invitedUserInfo },
  } = useContext(RootStoreContext);
  const { inviteToken } = useParams();
  const { errors, values, setValue, validateField, handleSubmit } = useForm<FieldValues>({
    defaultValues: {
      [SignUpFieldName.CreatePassword]: '',
      [SignUpFieldName.ConfirmPassword]: '',
    },
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case SignUpFieldName.CreatePassword: {
          return validateRequiredField(value, fieldName) || validatePassword(values[SignUpFieldName.CreatePassword]);
        }
        case SignUpFieldName.ConfirmPassword: {
          return values[SignUpFieldName.CreatePassword] !== value ? 'Entered passwords do not match' : '';
        }
        default:
          return '';
      }
    },
  });

  const getInvitedUser = async (invitedToken: string) => {
    try {
      await authStore.getInvitedUser(invitedToken);
    } catch (error) {
      navigator(ApplicationRoute.Login);
    }
  };

  useEffect(() => {
    if (inviteToken) {
      getInvitedUser(inviteToken);
    }
  }, [inviteToken, authStore]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setValue(name, value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;

    validateField(name, values[name]);
  };

  const handleSubmitPassword = handleSubmit(async () => {
    if (!inviteToken || !invitedUserInfo) {
      return;
    }

    try {
      await authStore.signUp({
        inviteToken: inviteToken,
        email: invitedUserInfo.email as string,
        password: values[SignUpFieldName.CreatePassword],
      });
      navigator(ApplicationRoute.ConfirmEmail);
    } catch (error: any) {
      notify(error.message, 'error');
    }
  });

  const handleLogin = () => {
    navigator(ApplicationRoute.Login);
  };

  const alreadyHaveAccount = (
    <div className={styles.main}>
      <h3 className={styles.title}>
        You already have an account. Move to{' '}
        <span className={styles.link} onClick={handleLogin}>
          Login
        </span>
      </h3>
    </div>
  );

  const passwordWidget = (
    <div className={styles.main}>
      <h3 className={styles.title}>Create Account</h3>
      <div className={styles.form}>
        <TextInput
          labelTitle={SignUpFieldName.Email}
          value={invitedUserInfo?.email}
          disabled={true}
          containerClassName={styles.emailInputContainer}
        />
        <div className={styles.inputsRow}>
          <PasswordInput
            containerClassName={styles.passwordInputContainer}
            labelTitle={SignUpFieldName.CreatePassword}
            name={SignUpFieldName.CreatePassword}
            tabIndex={2}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values[SignUpFieldName.CreatePassword]}
            errorMessage={errors[SignUpFieldName.CreatePassword]}
            required
          />
          <PasswordInput
            labelTitle={SignUpFieldName.ConfirmPassword}
            name={SignUpFieldName.ConfirmPassword}
            tabIndex={2}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values[SignUpFieldName.ConfirmPassword]}
            containerClassName={styles.passwordInputContainer}
            errorMessage={errors[SignUpFieldName.ConfirmPassword]}
            required
          />
        </div>
        <PasswordValidationPoints password={values[SignUpFieldName.CreatePassword]} />
        <div className={styles.buttonsLayout}>
          <Button className={styles.button} onClick={handleSubmitPassword}>
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
  const content = invitedUserInfo?.accountExist ? alreadyHaveAccount : passwordWidget;

  return isLoading ? <Spinner /> : <>{content}</>;
};

export default observer(SignUpPage);
